/**
 * useMarken — Hook für Marken-Stammdaten (Rechtsträger)
 * Issue #278 / #260 — Verwaltet Marken inkl. rechtlicher Angaben
 * (HRB, USt-ID, IBAN) und Branding-Assets (Logo, Briefkopf, Footer).
 *
 * Trennung zu useMarkenConfig (in useAdminSettings): useMarkenConfig
 * verwaltet visuelles Branding (Farben, KI-Anweisungen, Design-Richtlinien)
 * pro Produktmarke. useMarken hier verwaltet Rechtsträger-Daten für die
 * ausgehende Korrespondenz (E-Mail-Footer, PDF-Briefbogen).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase as defaultClient } from '@/lib/supabase';
import type { Marke } from '@/lib/database.types';
import { toast } from 'sonner';

const BUCKET = 'marken-assets';

export type MarkeInsert = Omit<Marke, 'id' | 'created_at' | 'updated_at'>;
export type MarkeUpdate = Partial<Omit<Marke, 'id'>>;

// Dateiendung für Asset-Uploads: nur übernehmen, wenn der Name einen Punkt
// enthält und der Teil hinter dem letzten Punkt kurz ist (keine Pfad-Tricks).
// Fallback `bin`, damit Storage-Pfade immer eine Extension haben.
function pickExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  if (idx <= 0 || idx === filename.length - 1) return 'bin';
  const ext = filename.slice(idx + 1).toLowerCase();
  return /^[a-z0-9]{1,8}$/.test(ext) ? ext : 'bin';
}

// Plain-JS-Service: kapselt die Supabase-Aufrufe ohne React-State, damit der
// CRUD-Flow im Vitest-Test direkt gegen einen gemockten Client geprüft werden
// kann (siehe tests/unit/marken-rechtstraeger-flow.test.ts).
export function createMarkenService(client: SupabaseClient = defaultClient) {
  const list = async (): Promise<Marke[]> => {
    const { data, error } = await client.from('marken').select('*').order('marken_name');
    if (error) throw error;
    return (data ?? []) as Marke[];
  };

  const create = async (item: MarkeInsert): Promise<void> => {
    const payload: MarkeInsert = {
      ...item,
      marken_name: item.marken_name.trim(),
      rechtstraeger: item.rechtstraeger.trim(),
    };
    const { error } = await client.from('marken').insert(payload);
    if (error) throw error;
  };

  const update = async (id: string, updates: MarkeUpdate): Promise<void> => {
    const payload: MarkeUpdate = { ...updates, updated_at: new Date().toISOString() };
    if (typeof payload.marken_name === 'string') payload.marken_name = payload.marken_name.trim();
    if (typeof payload.rechtstraeger === 'string') payload.rechtstraeger = payload.rechtstraeger.trim();
    const { error } = await client.from('marken').update(payload).eq('id', id);
    if (error) throw error;
  };

  const remove = async (id: string): Promise<void> => {
    const { error } = await client.from('marken').delete().eq('id', id);
    if (error) throw error;
  };

  const uploadAsset = async (
    markeId: string,
    file: File,
    kind: 'logo' | 'briefkopf',
  ): Promise<string> => {
    const path = `${markeId}/${kind}.${pickExtension(file.name)}`;
    const { error } = await client.storage
      .from(BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type || undefined });
    if (error) throw error;
    return path;
  };

  const getAssetUrl = async (path: string | null | undefined): Promise<string | null> => {
    if (!path) return null;
    const { data, error } = await client.storage.from(BUCKET).createSignedUrl(path, 3600);
    if (error) return null;
    return data?.signedUrl ?? null;
  };

  return { list, create, update, remove, uploadAsset, getAssetUrl };
}

export function useMarken() {
  const [data, setData] = useState<Marke[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => createMarkenService(), []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await service.list();
      setData(rows);
      setError(null);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    reload();
  }, [reload]);

  const create = useCallback(
    async (item: MarkeInsert) => {
      try {
        await service.create(item);
      } catch (e: any) {
        toast.error(`Fehler: ${e?.message ?? String(e)}`);
        return false;
      }
      toast.success('Marke angelegt');
      reload();
      return true;
    },
    [reload, service],
  );

  const update = useCallback(
    async (id: string, updates: MarkeUpdate) => {
      try {
        await service.update(id, updates);
      } catch (e: any) {
        toast.error(`Fehler: ${e?.message ?? String(e)}`);
        return false;
      }
      toast.success('Marke aktualisiert');
      reload();
      return true;
    },
    [reload, service],
  );

  const remove = useCallback(
    async (id: string) => {
      try {
        await service.remove(id);
      } catch (e: any) {
        toast.error(`Fehler: ${e?.message ?? String(e)}`);
        return false;
      }
      toast.success('Marke gelöscht');
      reload();
      return true;
    },
    [reload, service],
  );

  const uploadAsset = useCallback(
    async (markeId: string, file: File, kind: 'logo' | 'briefkopf'): Promise<string | null> => {
      try {
        return await service.uploadAsset(markeId, file, kind);
      } catch (e: any) {
        toast.error(`Upload fehlgeschlagen: ${e?.message ?? String(e)}`);
        return null;
      }
    },
    [service],
  );

  const getAssetUrl = useCallback(
    (path: string | null | undefined) => service.getAssetUrl(path),
    [service],
  );

  return { data, loading, error, refetch: reload, create, update, remove, uploadAsset, getAssetUrl };
}
